import * as Yup from "yup";

import { PricingTierFormValues } from "services/api/pricing/types";

/**
 * Obrazac cjenika, shema po koraku čarobnjaka.
 *
 * Tvrde brane stoje u modelu na backendu, jer se sučelje da zaobići. Ovdje su provjere koje
 * korisniku štede odlazak na server, pa dio pravila namjerno stoji na oba mjesta: struktura
 * razreda se provjerava već na koraku, da se rupa ne otkrije tek na zadnjem koraku.
 * Backend ostaje mjerodavan.
 */

/**
 * Shema prvog koraka.
 *
 * Najraniji dopušten dan nije uvijek danas: verzija koja vrijedi danas smije zadržati svoj
 * datum, pa se za nju granica spušta na taj dan. Starije verzije se ne prepisuju.
 * Ista brana stoji i na backendu — ovdje je samo da korisnik ne mora do servera po odgovor.
 */
export const pricingBasicsStepSchema = (minValidFrom: Date) =>
  Yup.object().shape({
    validFrom: Yup.string()
      .nullable()
      .required("Datum početka važenja je obavezan")
      .test(
        "not-before-min",
        `Datum početka važenja ne smije biti raniji od ${minValidFrom.toLocaleDateString("hr-HR")}`,
        (value) => !value || new Date(value) >= minValidFrom
      ),
  });

export const PRICING_COMBINATIONS_STEP_SCHEMA = Yup.object().shape({
  combinations: Yup.array().of(
    Yup.object().shape({
      packCodes: Yup.array()
        .of(Yup.string())
        .min(1, "Kombinacija mora sadržavati barem jedan paket"),
      price: Yup.number()
        .nullable()
        .required("Cijena je obavezna")
        .min(0, "Cijena ne smije biti negativna"),
    })
  ),
});

export const PRICING_TIERS_STEP_SCHEMA = Yup.object().shape({
  priceTiers: Yup.array()
    .of(
      Yup.object().shape({
        fromVehicle: Yup.number()
          .nullable()
          .required("Početak razreda je obavezan")
          .min(1, "Razred počinje od 1"),
        toVehicle: Yup.number().nullable(),
        ratePercent: Yup.number()
          .nullable()
          .required("Stopa je obavezna")
          .min(0, "Stopa ne smije biti negativna"),
      })
    )
    .min(1, "Mora postojati barem jedan razred")
    /*
      Struktura ljestvice, ista pravila kao `PriceTierCalculator.Validate` na backendu.
      Bez ovoga se preklopljeni ili prekinuti razredi otkriju tek pri spremanju, na zadnjem
      koraku, kad je korisnik već otišao dalje.
    */
    .test("tier-structure", "Razredi nisu ispravni", function (tiers) {
      const list = (tiers ?? []) as PricingTierFormValues[];

      // Prazan popis hvata `min`, a nepopunjena polja njihove vlastite provjere. Dok su
      // one crvene, o strukturi se nema što reći.
      if (list.length === 0 || list.some((tier) => tier.fromVehicle === null)) {
        return true;
      }

      const ordered = [...list].sort((a, b) => a.fromVehicle! - b.fromVehicle!);

      if (ordered[0].fromVehicle !== 1) {
        return this.createError({ message: "Prvi razred mora počinjati od 1" });
      }

      if (ordered.filter((tier) => tier.toVehicle === null).length !== 1) {
        return this.createError({
          message: "Točno jedan razred smije biti otvoren prema beskonačnosti",
        });
      }

      if (ordered[ordered.length - 1].toVehicle !== null) {
        return this.createError({ message: "Otvoreni razred mora biti posljednji" });
      }

      for (let i = 0; i < ordered.length - 1; i++) {
        const current = ordered[i];
        const next = ordered[i + 1];

        if (next.fromVehicle !== current.toVehicle! + 1) {
          return this.createError({
            message: `Razredi moraju biti spojni. Između ${current.toVehicle} i ${next.fromVehicle} je rupa ili preklapanje`,
          });
        }
      }

      return true;
    }),
});
